import { Box, Button, MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  IOrderReturnSearchParams,
  ReturnConfirmationFilter,
} from '../../types/api/request/returnreceptionrequest';
import { ReturnSource, ReturnStatus } from '../../types/api/response/returnreceptionresponse';

const DATE_FORMAT = 'YYYY-MM-DD';

// Full width on a phone so the filters stack; their natural width from sm up, where they wrap.
const fieldSx = (minWidth: number) => ({ minWidth, flex: { xs: '1 1 100%', sm: '0 0 auto' } });

/** '' = the default: everything that has come back (received, short or resolved). */
export type AllReturnsStatusFilter = '' | ReturnStatus | 'ANY';

export interface AllReturnsFilterValue {
  status: AllReturnsStatusFilter;
  source: '' | ReturnSource;
  confirmation: '' | ReturnConfirmationFilter;
  /** YYYY-MM-DD, inclusive */
  receivedFrom: string;
  receivedTo: string;
}

export const EMPTY_ALL_RETURNS_FILTER: AllReturnsFilterValue = {
  status: '',
  source: '',
  confirmation: '',
  receivedFrom: '',
  receivedTo: '',
};

const COME_BACK_STATUSES = 'RECEIVED,SHORT,RESOLVED';

/** List-endpoint query for the "All returns" tab — newest received first. */
export const toAllReturnsSearchParams = (
  value: AllReturnsFilterValue,
): IOrderReturnSearchParams => ({
  status:
    value.status === '' ? COME_BACK_STATUSES : value.status === 'ANY' ? undefined : value.status,
  source: value.source || undefined,
  confirmation_state: value.confirmation || undefined,
  received_from: value.receivedFrom || undefined,
  received_to: value.receivedTo || undefined,
  sort: 'receivedAt,desc',
});

// YYYY-MM-DD is ISO 8601, which dayjs parses without the customParseFormat plugin.
const toDayjs = (value: string): Dayjs | null => (value ? dayjs(value) : null);
const fromDayjs = (value: Dayjs | null): string =>
  value && value.isValid() ? value.format(DATE_FORMAT) : '';

interface Props {
  value: AllReturnsFilterValue;
  onChange: (change: Partial<AllReturnsFilterValue>) => void;
  onClear: () => void;
}

export const AllReturnsFilters = ({ value, onChange, onClear }: Props) => {
  const isFiltered = Object.values(value).some(Boolean);
  const from = toDayjs(value.receivedFrom);
  const to = toDayjs(value.receivedTo);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
        <TextField
          select
          size="small"
          label="Status"
          value={value.status}
          onChange={(e) => onChange({ status: e.target.value as AllReturnsStatusFilter })}
          sx={fieldSx(220)}
        >
          <MenuItem value="">Received, short or resolved</MenuItem>
          <MenuItem value="RECEIVED">Received</MenuItem>
          <MenuItem value="SHORT">Short</MenuItem>
          <MenuItem value="RESOLVED">Resolved</MenuItem>
          <MenuItem value="EXPECTED">Expected (not yet back)</MenuItem>
          <MenuItem value="ANY">Any status</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Source"
          value={value.source}
          onChange={(e) => onChange({ source: e.target.value as AllReturnsFilterValue['source'] })}
          sx={fieldSx(180)}
        >
          <MenuItem value="">Any source</MenuItem>
          <MenuItem value="SUPERPRO">SuperPro</MenuItem>
          <MenuItem value="INTERNAL">Internal (bay intake)</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Confirmation"
          value={value.confirmation}
          onChange={(e) =>
            onChange({ confirmation: e.target.value as AllReturnsFilterValue['confirmation'] })
          }
          sx={fieldSx(180)}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="SENT">Sent</MenuItem>
          <MenuItem value="FAILED">Failed or stuck</MenuItem>
          <MenuItem value="NONE">None (internal)</MenuItem>
        </TextField>
        <DatePicker
          label="Received from"
          value={from}
          maxDate={to ?? undefined}
          onChange={(val) => onChange({ receivedFrom: fromDayjs(val) })}
          slotProps={{ textField: { size: 'small', sx: fieldSx(0) }, field: { clearable: true } }}
        />
        <DatePicker
          label="Received to"
          value={to}
          minDate={from ?? undefined}
          onChange={(val) => onChange({ receivedTo: fromDayjs(val) })}
          slotProps={{ textField: { size: 'small', sx: fieldSx(0) }, field: { clearable: true } }}
        />
        {isFiltered && (
          <Button size="small" onClick={onClear}>
            Clear filters
          </Button>
        )}
      </Box>
    </LocalizationProvider>
  );
};
